import { BigNumber } from "bignumber.js";
import moment from "moment";

/**
 * The function takes a pattern parameter, which is a string representing the regex pattern.
 * @param {string} value - The value from field value.
 * @param {string} pattern -  The pattern is passed as a string and converted to a RegExp object inside the function.
 *The Regex Pattern is used for matching value with a regex.
 * @returns {string} - Returns result of value normalization.
 */
const normalizeField = (value: string | null | undefined, pattern: string) => {
	if (value === null || value === undefined) {
		return value;
	}

	const regex = new RegExp(pattern, "g");
	const result = value.replace(regex, "");

	return result;
};

/**
 * Removes any characters from the input value that are not alphanumeric, spaces and "%" character.
 * The function calls normalizeField with the appropriate regex pattern to allow alphanumeric characters, spaces, and the "%" character.
 * @param {string} value - The value from field value.
 * @param {string} pattern - The pattern is used for matching value with a regex.
 * @returns {string} - The normalized value.
 */

export const alphaNumericWithSpacesAndPercentsNormalizer = (value: string) =>
	normalizeField(value, "[^\\da-zA-Z\\s%]");

/**
 * Removes any characters from the input value that are not alphanumeric (letters and digits).
 * The function calls normalizeField with the appropriate regex pattern to allow only alphanumeric characters.
 * @param {string} value - The value to be normalized.
 * @returns {string} - The normalized value.
 */
export const alphaNumericNormalizer = (value: string) =>
	normalizeField(value, "[^\\da-zA-Z]");

/**
 * Replaces multiple spaces in the input value with a single space.
 * @param {string} value - The string to be normalized.
 * @returns {string} - The normalized string with single spaces.
 */
export const trimMidSentenceNormalizer = (value: string) => {
	if (!value) {
		return value;
	}

	const trimmedValue = value.replace(/ {2,}/g, " ");
	return trimmedValue;
};
/**
 * Removes whitespace from the beginning and end of the input value (a string or a number).
 * @param {string | number | null | undefined} value - The value to be trimmed.
 * @returns {string | null | undefined} - The trimmed value, or the original value if it is not a string or number.
 */
export const trimNormalizer = value => {
	if (value === null || value === undefined || value === "") {
		return value;
	}
	if (typeof value === "string" || value instanceof String) {
		return value.trim();
	}
	if (typeof value === "number") {
		return value.toString().trim();
	}
};

/**
 * Normalizes the input value by removing a specific symbol from the start of the string.
 * @param {string | null | undefined} value - The value to be normalized.
 * @param {string} symbol - The symbol to be removed from the start of the string.
 * @returns {string | null | undefined} - The normalized value, or the original value if it is not a string.
 */

export const trimStartSymbolNormalizer = (symbol: string) => value => {
	if (typeof value === "string" || value instanceof String) {
		let startIndex = 0;
		value.split("").some((currentSymbol, index) => {
			if (currentSymbol !== symbol) {
				startIndex = index;
				return true;
			}
			return false;
		});
		return value.slice(startIndex);
	}
	return value;
};

/**
 * Formats the input value according to the properties of the 'format' object.
 * @param {string | number | null | undefined} value - The value to be formatted.
 * @returns {string | null | undefined} - The formatted value, or the original value if it is not a string or number.
 * Returns a string rounded to 'signs' decimal places and
 */
export const formatFloat =
	(signs = 2, groupSeparator = "", groupSize = 0) =>
	(value, prefix = "", suffix = "") => {
		const format = {
			prefix,
			decimalSeparator: ".",
			groupSeparator,
			groupSize,
			secondaryGroupSize: 0,
			fractionGroupSeparator: "",
			fractionGroupSize: 0,
			suffix,
		};

		if (value === null || value === undefined || value === "") {
			return value;
		}

		if (typeof value === "string") {
			value = value.replaceAll(",", "");
		}

		let number = new BigNumber(value);
		BigNumber.config({ FORMAT: format });

		return number.isNaN() ? null : number.toFormat(signs);
	};

/**
 * Formats the input value by replacing commas with periods and ensuring it is a valid floating-point number.
 * @param {string | number | null | undefined} value - The value to be formatted.
 * @returns {string | null | undefined} - The formatted value, or the original value if it is not a string or number.
 */
// Use only for normalization in input fields
export const formatCommaAndFloat =
	(signs, canBeNegative = false) =>
	value => {
		if (value === null || value === undefined || value === "") {
			return value;
		}

		const format = {
			prefix: "",
			decimalSeparator: ".",
			groupSeparator: ",",
			groupSize: 3,
			secondaryGroupSize: 0,
			fractionGroupSeparator: "",
			fractionGroupSize: 0,
			suffix: "",
		};
		BigNumber.config({ FORMAT: format });

		if (isNaN(value)) {
			if (canBeNegative) {
				// eslint-disable-next-line no-useless-escape
				value = value.replace(/[^-0-9\.]+/g, "");
				// Remove minus besides beginning
				if (value.match(/^-/g)) {
					value = `-${value.slice(1).replaceAll("-", "")}`;
				} else {
					value = value.replaceAll("-", "");
				}
			} else {
				// eslint-disable-next-line no-useless-escape
				value = value.replace(/[^0-9\.]+/g, "");
			}

			value = value.replaceAll(",", ""); //avoid fall in isNaN

			if (value.split(".").length - 1 > 1) {
				//delete duplicate points, such as: 3.32.122
				value = value.replace(".", "");
			}
		}

		if (value === "-" || value === "" || value === "." || value === "-.") {
			return value;
		}

		let [integer, fraction] = value.toString().split(".");

		// Cut value
		if (fraction?.length > signs) {
			fraction = fraction.slice(0, signs);
			value = `${integer}.${fraction}`;
		}
		let bigNumber = new BigNumber(value);

		// Because bignumber formats -0 to 0
		if (integer === "-0") {
			return value;
		}

		if (fraction === "") {
			return `${bigNumber.toFormat()}.`;
		} else if (fraction?.length > 0) {
			return bigNumber.toFormat(fraction?.length);
		} else {
			return bigNumber.toFormat();
		}
	};

/**
 * Normalizes the input value to a floating-point number with up to two decimal places.
 * @param {string | number | null | undefined} value - The value to be normalized.
 * @returns {string | null | undefined} - The normalized value, or the original value if it is not a string or number.
 */
export const float2Normalizer = formatFloat(2);
/**
 * Normalizes the input value to a floating point number with up to four decimal places.
 * @param {string | number | null | undefined} value - The value to be normalized.
 * @returns {string | null | undefined} - The normalized value, or the original value if it is not a string or number.
 */
export const float4Normalizer = formatFloat(4);
/**
 * Normalizes the input value to a floating-point number with up to six decimal places.
 * @param {string | number | null | undefined} value - The value to be normalized.
 * @returns {string | null | undefined} - The normalized value, or the original value if it is not a string or number.
 */
export const float6Normalizer = formatFloat(6);

/**
 * Normalize for floating-point numbers with 2 decimal places and ',' as the thousands separator.
 *
 * Example:
 * - Input: 123456.789
 * - Output: "123,456.79"
 */
export const floatWithComma2Normalizer = formatFloat(2, ",", 3);
/**
 * Normalize for floating-point numbers with 6 decimal places and ',' as the thousands separator.
 *
 * Example:
 * - Input: 123456.78912345
 * - Output: "123,456.789123"
 */
export const floatWithComma6Normalizer = formatFloat(6, ",", 3);
/**
 * Normalize for numbers with 2 decimal places, comma grouping, and no negative values.
 * Example:
 * - Input: 12345.678
 * - Output: "12,345.68"
 */
export const commaAndFloat2Normalizer = formatCommaAndFloat(2);
/**
 * Normalize for numbers with 2 decimal places, comma grouping, and support for negative values.
 * Example:
 * - Input: -12345.678
 * - Output: "-12,345.68"
 */
export const commaAndFloatNegative2Normalizer = formatCommaAndFloat(2, true);

const taxIdNormalizerCommon = (
	value: string,
	allowAsterisk: boolean = false,
) => {
	if (!value) {
		return value;
	}

	const onlyNums = allowAsterisk
		? value.toString().replace(/[^\d*]/g, "")
		: value.toString().replace(/[^\d]/g, "");
	if (onlyNums.length <= 2) {
		return onlyNums;
	}
	return `${onlyNums.slice(0, 2)}-${onlyNums.slice(2, 9)}`;
};

/**
 * Normalizes a tax ID by removing non-numeric characters and formatting it as XX-XXXXXX.
 * @param value - The input value, which could be a string, null, or undefined.
 * @returns The normalized value, or the original value if it is not a number.
 */

export const taxIdNormalizer = value => taxIdNormalizerCommon(value, false);

/**
 * Normalizes a tax ID by removing non-numeric characters and formatting it as XX-XXXXXX including asterisk.
 * @param value - The input value, which could be a string, null, or undefined.
 * @returns The normalized value, or the original value if it is not a number.
 */

export const taxIdNormalizerAsterisk = value =>
	taxIdNormalizerCommon(value, true);

const SSNNormalizerCommon = (
	value: string | undefined | null,
	allowAsterisk: boolean = false,
) => {
	if (!value) {
		return value;
	}

	const onlyNums = allowAsterisk
		? value.toString().replace(/[^\d*]/g, "")
		: value.toString().replace(/[^\d]/g, "");
	if (onlyNums.length <= 3) {
		return onlyNums;
	}
	if (onlyNums.length <= 5) {
		return `${onlyNums.slice(0, 3)}-${onlyNums.slice(3, 5)}`;
	}
	return `${onlyNums.slice(0, 3)}-${onlyNums.slice(3, 5)}-${onlyNums.slice(
		5,
		9,
	)}`;
};

/**
 * Normalizes a SSN by removing non-numeric characters and formatting it as XXX-XX-XXXX.
 * @param value - The input value to normalize, which could be a string, null, or undefined.
 * @returns The normalized value, or the original value if it is not a number.
 */
export const SSNNormalizer = value => SSNNormalizerCommon(value, false);

/**
 * Normalizes a SSN by removing non-numeric characters and formatting it as XXX-XX-XXXX including asterisk.
 * @param value - The input value to normalize, which could be a string, null, or undefined.
 * @returns The normalized value, or the original value if it is not a number.
 */
export const SSNNormalizerAsterisk = value => SSNNormalizerCommon(value, true);

/**
 * Normalizes a credit card expiration date by formatting it as MM/YY.
 * @param value - The input value to normalize, which could be a string, null, or undefined.
 * @returns The normalized value (MM/YY), or the original value if it is not a number.
 */

export const creditCardExpDateNormalizer = value => {
	if (!value) {
		return value;
	}

	let onlyNums = value
		.replace(/^(\d\d)(\d)$/g, "$1/$2")
		.replace(/[^\d/]/g, "")
		.substr(0, 5);

	return onlyNums;
};

/**
 * Normalizes the input value as a date string when the input field loses focus.
 * @param {string | null | undefined} value - The value to be validated and formatted.
 * @returns {string | null | undefined} - The formatted date string, or the original value if it is not a valid date.
 */

export const businessDateNormalizer = value => {
	if (!value) {
		return value;
	}

	if (value.length !== 10) {
		return null;
	}

	let date = moment(value, "MM/DD/YYYY");
	if (!date.isValid()) {
		return null;
	} else {
		return value;
	}
};

/**
 * Normalizes the input value by converting it to a lowercase string.
 * @param {string | number | null | undefined} value - The value to be normalized.
 * @returns {string | null | undefined} - The normalized value in lowercase, or the original value if it is not a string or number.
 */
export const lowerCaseNormalizer = (
	value: string | number | null | undefined,
) => {
	if (value === null || value === undefined || value === "") {
		return value;
	}
	if (typeof value === "string" || value) {
		return (value as string).toLowerCase();
	}
	if (typeof value === "number") {
		return value.toString().toLowerCase();
	}
	return value;
};
